/**
 * Query-cache persister — survives the React Query cache across hard
 * reloads via localStorage.
 *
 * Pattern: TanStack's `PersistQueryClientProvider` with a sync-storage
 * persister. On boot, the cache rehydrates from localStorage
 * synchronously (before first paint), so navigations to common routes
 * (audits landing, page detail, sidebar switcher's site list) render
 * with cached data *immediately* instead of paying the auth-floor +
 * network round-trip again.
 *
 * Whitelisted queries only — see `shouldDehydrateQuery` below. We
 * persist data that is:
 *   - high-traffic across navigations (workspace, sites list)
 *   - cheap to serve from cache (KV-backed pages-with-audits)
 *   - stable identifiers (page / site detail, used by breadcrumbs)
 *
 * We deliberately DON'T persist:
 *   - audit detail (large, polls while running, stale within minutes)
 *   - Lighthouse reports (multi-MB blobs)
 *   - usage/metrics that change every minute
 *
 * Safety:
 *   - `buster` invalidates the entire persisted cache when the app's
 *     schema-bump version changes — bump `CACHE_BUSTER` whenever a
 *     query response shape changes in a way that would break
 *     deserialisation.
 *   - `maxAge` ages out stale data so a user returning after a long
 *     break doesn't see ancient values.
 *   - Sign-out path (`SidebarUserPill`, `CommandPalette` "Sign out"
 *     action) calls `queryClient.clear()` BEFORE Clerk's signOut —
 *     that propagates to the persister so the next user on the same
 *     browser sees a clean cache.
 *   - The persisted blob is keyed by `STORAGE_KEY`; rotating that key
 *     (in addition to bumping the buster) is the nuclear option
 *     when a refactor breaks the storage shape itself.
 */
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { Query } from '@tanstack/react-query';

const STORAGE_KEY = 'wv.dashboard.query-cache.v1';

/**
 * Bump on every change to a query response shape that would break
 * deserialisation. We compute it as a constant rather than reading
 * from package.json so HMR doesn't churn the cache during local dev.
 */
export const CACHE_BUSTER = 'v1-2026-05-28';

/** 4 hours. Older data is dropped on hydrate. */
export const CACHE_MAX_AGE_MS = 4 * 60 * 60 * 1000;

/**
 * Decide whether a given query gets persisted. Whitelist by queryKey
 * prefix — anything not explicitly listed stays in-memory only.
 */
export function shouldDehydrateQuery(query: Query): boolean {
  const key = query.queryKey;
  if (!Array.isArray(key) || key.length === 0) return false;
  const head = key[0];

  // Workspace bootstrap — user, orgs, plan, active_usage, regions.
  if (head === 'workspace') return true;

  // Sites: list (used by every shell) + detail (breadcrumbs) +
  // pages-with-audits (audits landing).
  if (head === 'sites') {
    const second = key[1];
    return second === 'list' || second === 'detail' || second === 'pages-with-audits';
  }

  // Pages: detail (breadcrumbs on /pages/:uuid + /audits/:auditId).
  if (head === 'pages') {
    const second = key[1];
    return second === 'detail';
  }

  // Nothing else (audits, lighthouse reports, usage, etc.) — too
  // large, too fresh-required, or both.
  return false;
}

/**
 * Stable persister instance for the app's lifetime. Returns `null`
 * when running outside a browser (SSR, tests without jsdom).
 */
export function createQueryPersister() {
  if (typeof window === 'undefined') return null;
  return createSyncStoragePersister({
    storage: window.localStorage,
    key: STORAGE_KEY,
    // 5MB localStorage caps loosely. We're well under (workspace ~5KB +
    // sites ~5KB + pages-with-audits ~5KB/site × few sites), but
    // protect against pathological growth on accounts with many sites.
    throttleTime: 1000,
  });
}
